import {coerceNotificationFeedRealtimeType} from "./notification-feed-topics";
import type {NotificationItem} from "../types";
import {dispatchCenterRequiresShift} from "@/src/modules/core/lib/dispatch-center-policy";
import {useMeStore} from "@/src/modules/core/store/me";

const GATE_DISPATCH_DEBOUNCE_MS = 2_000;
const recentGateHandling = new Map<string, number>();

function complianceGateKey(kind: "break" | "shift-end", id: number): string {
  return kind === "break" ? `break:${id}` : `shift-end:${id}`;
}

/** True when this obligation was handled recently (modal path or synthetic dispatch). */
export function wasComplianceGateHandledRecently(kind: "break" | "shift-end", id: number): boolean {
  const last = recentGateHandling.get(complianceGateKey(kind, id));
  return last != null && Date.now() - last < GATE_DISPATCH_DEBOUNCE_MS;
}

/**
 * Claim handling for an obligation. Returns false if the same gate ran within the debounce window.
 * Call at the start of modal realtime handlers (Pusher or synthetic `break:alert`).
 */
export function beginComplianceGateHandling(kind: "break" | "shift-end", id: number): boolean {
  const key = complianceGateKey(kind, id);
  if (wasComplianceGateHandledRecently(kind, id)) {
    return false;
  }
  recentGateHandling.set(key, Date.now());
  return true;
}

function readPayloadNumber(
  payload: Record<string, unknown> | undefined,
  key: string,
): number | undefined {
  const value = payload?.[key];
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** Opens the break-notice gate — same contract as the Reverb `break:alert` event. */
export function dispatchBreakNoticeGate(shiftBreakId: number): void {
  if (wasComplianceGateHandledRecently("break", shiftBreakId)) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent("break:alert", {
      detail: {shift_break_id: shiftBreakId},
    }),
  );
}

/** Opens the shift-end gate — same contract as the Reverb `shift:end-alert` event. */
export function dispatchShiftEndGate(shiftId: number): void {
  if (wasComplianceGateHandledRecently("shift-end", shiftId)) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent("shift:end-alert", {
      detail: {shift_id: shiftId},
    }),
  );
}

/** Feed row → compliance modal (fallback when inbox arrives without custom Reverb event). */
export function dispatchShiftComplianceGateFromNotification(item: NotificationItem): void {
  const me = useMeStore.getState().me;
  if (!dispatchCenterRequiresShift(me)) {
    return;
  }

  if (item.type === "shift.break_alert") {
    const shiftBreakId =
      readPayloadNumber(item.payload, "shift_break_id") ??
      (typeof item.subject.id === "number" ? item.subject.id : undefined);
    if (shiftBreakId != null) {
      dispatchBreakNoticeGate(shiftBreakId);
    }
    return;
  }

  if (item.type === "shift.end_alert") {
    const shiftId =
      readPayloadNumber(item.payload, "shift_id") ??
      (typeof item.subject.id === "number" ? item.subject.id : undefined);
    if (shiftId != null) {
      dispatchShiftEndGate(shiftId);
    }
  }
}

/** Web Push `notification.data` → compliance modal after SW click navigates to `/home`. */
export function dispatchShiftComplianceGateFromPushData(data: Record<string, unknown>): void {
  const me = useMeStore.getState().me;
  if (!dispatchCenterRequiresShift(me)) {
    return;
  }

  const type = coerceNotificationFeedRealtimeType(typeof data.type === "string" ? data.type : null);
  if (!type) {
    return;
  }

  if (type === "shift.break_alert") {
    const shiftBreakId = readPayloadNumber(data, "shift_break_id");
    if (shiftBreakId != null) {
      dispatchBreakNoticeGate(shiftBreakId);
    }
    return;
  }

  if (type === "shift.end_alert") {
    const shiftId = readPayloadNumber(data, "shift_id");
    if (shiftId != null) {
      dispatchShiftEndGate(shiftId);
    }
  }
}
